import { getBackendUrl } from '../config/api';
import { trackFunnel, getSessionId } from './analytics';

/**
 * Kundali Snapshot Service (D4)
 * Caches snapshot per profile for the session; invalidates after midnight IST or profile switch.
 */

export function getTodayDateIST() {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  } catch {
    // Fallback if Intl timeZone is unsupported
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(now.getTime() + istOffset);
    return istDate.toISOString().split('T')[0];
  }
}

export function getSnapshotCacheKey(profileId, lang = 'hinglish') {
  const pId = profileId || 'self';
  const today = getTodayDateIST();
  return `jp_snapshot_${pId}_${today}_${lang}`;
}

export function getCachedSnapshot(profileId, lang = 'hinglish') {
  try {
    const key = getSnapshotCacheKey(profileId, lang);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.data && parsed.dateIST === getTodayDateIST()) {
      return parsed.data;
    }
  } catch (e) {
    console.warn('[SNAPSHOT-CACHE] Read error:', e);
  }
  return null;
}

export function setCachedSnapshot(profileId, lang = 'hinglish', data) {
  try {
    if (!data) return;
    const key = getSnapshotCacheKey(profileId, lang);
    const item = {
      dateIST: getTodayDateIST(),
      profileId: profileId || 'self',
      lang,
      timestamp: Date.now(),
      data,
    };
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (e) {
    console.warn('[SNAPSHOT-CACHE] Write error:', e);
  }
}

/**
 * Fetch Kundali Snapshot from backend API.
 * Uses cached result when valid unless forceRefresh is true.
 */
export async function fetchSnapshot({
  profileId = 'self',
  dob,
  tob,
  place,
  lat,
  lon,
  tz,
  intent = 'general',
  lang = 'hinglish',
  name,
  leadId,
  sessionId,
  token,
  forceRefresh = false,
} = {}) {
  const effectiveSessionId = sessionId || (typeof getSessionId === 'function' ? getSessionId() : undefined);

  if (!forceRefresh && !dob && !tob) {
    const cached = getCachedSnapshot(profileId, lang);
    if (cached) {
      return cached;
    }
  }

  const backendUrl = getBackendUrl();
  const headers = {
    'Content-Type': 'application/json',
  };

  const authToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const body = {
    intent,
    lang,
    ...(effectiveSessionId ? { session_id: effectiveSessionId } : {}),
  };

  if (profileId && profileId !== 'self') {
    body.profile_id = profileId;
  }
  if (name) body.name = name;
  if (leadId) body.lead_id = leadId;
  if (dob) body.dob = dob;
  if (tob) body.tob = tob;
  if (place) body.place = place;
  if (lat != null) body.lat = lat;
  if (lon != null) body.lon = lon;
  if (tz) body.tz = tz;

  const res = await fetch(`${backendUrl}/api/snapshot`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Failed to fetch snapshot' }));
    throw new Error(err.detail || `Snapshot request failed (${res.status})`);
  }

  const data = await res.json();
  if (profileId) {
    setCachedSnapshot(profileId, lang, data);
  }
  return data;
}

export function emitSnapshotViewed({ intent, profileId, leadId } = {}) {
  try {
    const sId = typeof getSessionId === 'function' ? getSessionId() : undefined;
    trackFunnel('snapshot_viewed', {
      session_id: sId,
      intent: intent || 'general',
      profile_id: profileId || 'self',
      ...(leadId ? { lead_id: leadId } : {}),
    });
  } catch (e) {
    console.warn('[ANALYTICS] snapshot_viewed failed:', e);
  }
}

export function emitSnapshotCtaTapped({ ctaType, chipText, intent, freeQuestionAvailable } = {}) {
  try {
    const sId = typeof getSessionId === 'function' ? getSessionId() : undefined;
    trackFunnel('snapshot_cta_tapped', {
      session_id: sId,
      cta_type: ctaType,
      chip_text: chipText,
      intent: intent || 'general',
      free_question_available: !!freeQuestionAvailable,
    });
  } catch (e) {
    console.warn('[ANALYTICS] snapshot_cta_tapped failed:', e);
  }
}
